import {
  CircularProgress,
  DialogActions,
  IconButton,
  InputAdornment,
  makeStyles,
  Popper,
} from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { useSnackbar } from 'notistack';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Field, Form } from 'react-final-form';
import authAPI from '../../api/auth';
import { COLORS } from '../../theme/variables';
import { isPassword } from '../../utils/String/Password';
import {
  checkCapitalCharacters,
  checkNumber,
  checkSpecialSymbols,
  isMobile,
} from '../../utils/functions';
import { Button, FormGroup, ModalV2, Text } from '../common';
import { TextFieldWrapper } from '../forms/wrappers';

interface PasswordResetModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface FormValues {
  oldPassword: string;
  newPassword: string;
}

interface ValidPasswordValue {
  icon: boolean;
  label: string;
}

interface ValidPassword {
  isMinimalLength: ValidPasswordValue;
  isSpecialSymbol: ValidPasswordValue;
  isNumbers: ValidPasswordValue;
  isCapitalCharacters: ValidPasswordValue;
}

type Errors = {
  [K in keyof FormValues]?: string;
};

const useStyles = makeStyles({
  actions: {
    padding: '32px 0 0',
  },
  popper: {
    minWidth: 220,
    minHeight: 100,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    boxShadow:
      '0px 0px 2px rgb(9 29 61 / 25%), 0px 8px 16px rgb(34 91 187 / 8%), 0px 8px 24px rgb(51 126 255 / 8%)',
    borderRadius: 5,
    padding: '5px 10px',
    marginTop: 7,
  },
  popperItems: {
    width: '100%',
    padding: '5px 10px',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  checkIcon: {
    color: COLORS.COLOR_GREEN_BASE,
    marginRight: 7,
    fontSize: 20,
  },
  errorIcon: {
    color: COLORS.COLOR_RED_BASE,
    marginRight: 7,
    fontSize: 20,
  },
  popperContainer: {
    background: COLORS.COLOR_ADDITIONAL_WHITE,
  },
});

const validateForm = (values: FormValues) => {
  const errors: Errors = {};
  if (values.newPassword?.trim() && !values.oldPassword?.trim()) {
    errors.oldPassword = 'Required';
  }
  if (values.oldPassword?.trim() && !values.newPassword?.trim()) {
    errors.newPassword = 'Required';
  }
  if (values.newPassword?.trim() && !isPassword(values.newPassword.trim())) {
    errors.newPassword = 'Not valid';
  }
  return errors;
};

function PasswordResetModal({
  open,
  onClose,
  onSuccess,
}: PasswordResetModalProps) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [isShownOldPassword, setIsShownOldPassword] = useState(false);
  const [isShownNewPassword, setIsShownNewPassword] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [valueNewPassword, setValueNewPassword] = useState<string>('');
  const popperBoundaryRef = useRef<HTMLDivElement | null>(null);

  const validNewPassword: ValidPassword = useMemo(() => {
    const valid = {
      isMinimalLength: { icon: false, label: 'Minimal length 8 symbols' },
      isSpecialSymbol: { icon: false, label: 'Special Symbols' },
      isNumbers: { icon: false, label: 'Numbers' },
      isCapitalCharacters: { icon: false, label: 'Capital characters' },
    };

    if (checkNumber(valueNewPassword)) {
      valid.isNumbers.icon = true;
    }
    if (checkSpecialSymbols(valueNewPassword)) {
      valid.isSpecialSymbol.icon = true;
    }
    if (valueNewPassword.length > 8) {
      valid.isMinimalLength.icon = true;
    }
    if (checkCapitalCharacters(valueNewPassword)) {
      valid.isCapitalCharacters.icon = true;
    }
    return valid;
  }, [valueNewPassword]);

  const handleFocus = (e: any) => {
    setAnchorEl(e.currentTarget);
  };

  const handleChangeNewPassword = (e: any) => {
    const value = e.target.value;
    setValueNewPassword(value);
  };

  const handleBlur = () => {
    setAnchorEl(null);
  };

  const handleSubmit = useCallback(
    async (formValues: FormValues) => {
      try {
        setIsLoading(true);
        await authAPI.updatePassword({
          oldPassword: formValues.oldPassword,
          newPassword: formValues.newPassword,
        });

        enqueueSnackbar('Password changed successfully', {
          variant: 'success',
        });
        setIsLoading(false);
        onClose();
        onSuccess?.();
      } catch (error: any) {
        setIsLoading(false);
        const messageError = `Unfortunately, an error occurred when changing the password.\n Details: ${error.response?.data?.message || 'Internal server error'
          }`;
        enqueueSnackbar(messageError, {
          variant: 'error',
          style: { whiteSpace: 'pre-line' },
        });
        throw error;
      }
    },
    [enqueueSnackbar, onClose, onSuccess],
  );

  const popperOpen = Boolean(anchorEl);
  const id = popperOpen ? 'simple-popper' : undefined;

  return (
    <ModalV2
      width='560px'
      open={open}
      onClose={onClose}
      ref={popperBoundaryRef}
      titleProps={{ title: 'Reset Password' }}>
      <Form
        validate={validateForm}
        onSubmit={handleSubmit}
        initialValues={{ oldPassword: '', newPassword: '' }}
        render={(formProps) => (
          <form noValidate>
            <FormGroup mobile={isMobile()}>
              <Field<string>
                name='oldPassword'
                testid='password-reset-old-password'
                label='Current password*'
                autoComplete='new-password'
                component={TextFieldWrapper}
                type={isShownOldPassword ? 'text' : 'password'}
                format={(value) => (value ? value.trim() : value)}
                formatOnBlur
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label='toggle password visibility'
                        edge='end'
                        onClick={() =>
                          setIsShownOldPassword(!isShownOldPassword)
                        }>
                        {isShownOldPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <div>
                <Field<string>
                  name='newPassword'
                  component={TextFieldWrapper}
                  label='New password'
                  testid='password-reset-new-password'
                  autoComplete='new-password'
                  type={isShownNewPassword ? 'text' : 'password'}
                  format={(value) => (value ? value.trim() : value)}
                  formatOnBlur
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  onChange={handleChangeNewPassword}
                  aria-describedby={id}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          aria-label='toggle password visibility'
                          edge='end'
                          onClick={() =>
                            setIsShownNewPassword(!isShownNewPassword)
                          }>
                          {isShownNewPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Popper
                  id={id}
                  anchorEl={anchorEl}
                  open={popperOpen}
                  placement='bottom-start'
                  container={popperBoundaryRef.current}
                  className={classes.popperContainer}>
                  <div className={classes.popper}>
                    {Object.values(validNewPassword).map(
                      (value: ValidPasswordValue, idx) => {
                        return (
                          <div key={idx} className={classes.popperItems}>
                            {value.icon ? (
                              <CheckCircleIcon className={classes.checkIcon} />
                            ) : (
                              <CancelIcon className={classes.errorIcon} />
                            )}
                            <Text variant='normal'>{value.label} </Text>
                          </div>
                        );
                      },
                    )}
                  </div>
                </Popper>
              </div>
            </FormGroup>
            <DialogActions className={classes.actions}>
              <Button
                variant='outlined'
                onClick={onClose}
                data-testid='password-reset-cancel'>
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  await formProps.handleSubmit();
                }}
                disabled={
                  isLoading ||
                  !formProps.valid ||
                  !formProps.values.newPassword?.trim() ||
                  !formProps.values.oldPassword?.trim()
                }
                data-testid='password-reset-save'>
                {isLoading ? (
                  <CircularProgress size={24} color='inherit' />
                ) : (
                  'Save'
                )}
              </Button>
            </DialogActions>
          </form>
        )}
      />
    </ModalV2>
  );
}

export default PasswordResetModal;
